import { brandLinka } from '@ads/tokens';
import { DashboardTickets } from '@linka/domain/dtos';
import { useTranslation } from '@linka/i18n';
import { formatNumber } from '@linka/utils';
import { Bar, BarChart, CartesianGrid, LabelList, XAxis, YAxis } from 'recharts';
import { ChartContainer, ChartTooltip, ChartTooltipContent } from '../../../chart/chart';
import { ChartCard, ChartCardContent, ChartCardHeader, ChartCardTitle, ExportChartTriggerButton } from '../components';

interface SalesByTicketsProps {
  data: DashboardTickets;
}
export const SalesByTicketsChart = ({ data }: SalesByTicketsProps) => {
  const t = useTranslation();
  const chartConfig = {
    label: {},
    totalSold: {
      label: t('sales'),
      color: brandLinka.baseColor.primary['500'],
    },
  };
  return (
    <ChartCard>
      <ChartCardHeader>
        <ChartCardTitle>{t('dashboard.charts.sales-by-ticket-type')}</ChartCardTitle>
        <ExportChartTriggerButton type='OVERVIEW_SALES_BY_TICKET' />
      </ChartCardHeader>
      {data.data.length === 0 ? (
        <ChartCardContent className='flex flex-1 items-center justify-center py-xl'>
          <span className='ts-paragraph-xs text-ctx-content-base'>{t('dashboard.charts.no-data')}</span>
        </ChartCardContent>
      ) : (
        <ChartCardContent className='flex'>
          <ChartContainer config={chartConfig} className='w-[60px]'>
            <BarChart data={data.data} responsive accessibilityLayer>
              <Bar dataKey='totalSold' fill='transparent' barSize={0} />

              <YAxis
                tickLine={false}
                tickMargin={10}
                axisLine={false}
                tickFormatter={(value) => formatNumber(value, { notation: 'compact' })}
                width={60}
              />
              <XAxis dataKey='label' height={100} />
            </BarChart>
          </ChartContainer>

          <div className='flex overflow-x-auto'>
            <ChartContainer config={chartConfig} className='w-full' dataLength={data.data.length}>
              <BarChart data={data.data} responsive accessibilityLayer>
                <Bar dataKey='totalSold' fill='var(--color-totalSold)' radius={[8, 8, 0, 0]}>
                  <LabelList
                    dataKey='totalSold'
                    position='top'
                    formatter={(value) => formatNumber(Number(value), { notation: 'compact' })}
                  />
                </Bar>
                <CartesianGrid vertical={false} />
                <XAxis
                  dataKey='label'
                  tick={{ textAnchor: 'start' }}
                  angle={45}
                  height={100}
                  tickFormatter={(label) => {
                    const labelLength = String(label).length;
                    const maxLength = 12;
                    return labelLength > maxLength ? `${String(label).substring(0, maxLength)}...` : label;
                  }}
                />

                <ChartTooltip content={<ChartTooltipContent />} />
              </BarChart>
            </ChartContainer>
          </div>
        </ChartCardContent>
      )}
    </ChartCard>
  );
};
